import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { Link } from "react-router-dom";
import { io } from "socket.io-client";
import { Button } from "reactstrap";
import Cookies from "js-cookie";

import {
  socketAtom,
  tenantIDAtom,
  restoringBackupAtom,
  recordDetailDrawerOpenCount,
  setSchemaAtom,
  queryBuilderDrawer,
  queryBuilderUpdatedSchema,
} from "../atoms/GlobalAtoms";
import useDeleteRecordOperations from "../api/api-hooks/deletedRecords/DeletedRecordsOperations";
import { newToastEvent, updateToastEvent } from "../utils/ToastUtil";
import { subitemDrawerAtom } from "../atoms/PageAtoms";
import RightArrow from "../elements/RightArrow";
import TenantAPI from "../api/api/TenantAPI";
import Constants from "../utils/Constants";

const getPort = () => {
  if (window && window.location && window.location.href) {
    if (window.location.href.indexOf("localhost") >= 0) {
      return Constants.API_URL.DEVELOPMENT;
    } else if (window.location.href.indexOf("app-staging") >= 0) {
      return Constants.API_URL.STAGING;
    } else {
      return Constants.API_URL.PRODUCTION;
    }
  }

  return "";
};

const socket = io(getPort(), {
  autoConnect: false,
  withCredentials: true,
});

const useSocketEvents = () => {
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);
  const queryBuilderDrawerOpen = useAtomValue(queryBuilderDrawer);
  const setQueryBuilderUpdatedSchema = useSetAtom(queryBuilderUpdatedSchema);
  const { performOperation } = useDeleteRecordOperations();
  const setSocket = useSetAtom(socketAtom);
  const setSchema = useSetAtom(setSchemaAtom);
  const setRestoring = useSetAtom(restoringBackupAtom);
  const recordDetailDrawerCount = useAtomValue(recordDetailDrawerOpenCount);
  const subitemDrawer = useAtomValue(subitemDrawerAtom);
  const tenantIDRef = useRef(tenantID);
  const userIDRef = useRef();
  const queryBuilderDrawerRef = useRef<boolean>();
  tenantIDRef.current = tenantID;
  userIDRef.current = queryClient.getQueryData<any>([
    "user",
    tenantID,
  ])?.user?.id;
  const setSchemaRef = useRef(setSchema);
  const queryClientRef = useRef(queryClient);
  const setRestoringRef = useRef(setRestoring);
  const drawerCountRef = useRef(recordDetailDrawerCount);
  const subitemDrawerRef = useRef(subitemDrawer);
  userIDRef.current = queryClient.getQueryData<any>([
    "user",
    tenantID,
  ])?.user?.id;

  setSchemaRef.current = setSchema;
  queryClientRef.current = queryClient;
  setRestoringRef.current = setRestoring;
  drawerCountRef.current = recordDetailDrawerCount;
  subitemDrawerRef.current = subitemDrawer;
  queryBuilderDrawerRef.current = queryBuilderDrawerOpen;
  tenantIDRef.current = tenantID;

  const startSocketConnection = useCallback((token: string) => {
    socket.auth = { token };
    socket.connect();
    setSocket(socket);
  }, []);

  const endSocketConnection = useCallback(() => {
    socket.disconnect();
    setSocket(null);
  }, []);

  useEffect(() => {
    const client = queryClientRef.current;

    socket.on(
      "shared-record-updated",
      (data: { type: string; recordID: string; moduleAPIName: string }) => {
        const { recordID, moduleAPIName } = data;
        const queryCache = client.getQueryCache();

        const liveRecordQueries = queryCache.findAll({ type: "active" });

        liveRecordQueries.forEach((query) => {
          if (query.queryKey.includes(recordID)) {
            // If the record query exists, invalidate it
            client.invalidateQueries({ queryKey: query.queryKey });
          }

          if (query.queryKey.includes(moduleAPIName)) {
            const records: any[] = (query?.state?.data as any)?.records ?? [];

            // Checking search queries that could contain the record
            if (records?.length > 0) {
              records.forEach((record) => {
                // If the record is in the search query, invalidate the search query
                if (record.id === recordID) {
                  client.invalidateQueries({ queryKey: query.queryKey });
                }
              });
            }
          }
        });
      }
    );

    //---------------------- Backup Events -------------------------
    socket.on("backup-success", (data) => {
      const { type, message } = data;
      updateToastEvent(type, message, "backup-success", {
        loading: false,
        autoClose: false,
      });

      // Invalidate backups query
      client.invalidateQueries({ queryKey: ["backupHistory"] });
    });

    socket.on("restore-backup", (data) => {
      if (data.type === "in progress") {
        setRestoringRef.current!(true);
      }

      if (data.type === "error") {
        newToastEvent("error", "Failed to restore backup.");
        setRestoringRef.current(false);
      }

      if (data.type === "success") {
        Cookies.set(
          "reloaded",
          JSON.stringify({ Type: "success", Date: data?.Date }),
          {
            sameSite: "strict",
            secure: window.location.origin.startsWith("https"),
          }
        );
        window.location.reload();
      }
    });

    socket.on("file-chunk-index", (data) => {
      if (data.success === "false") {
        newToastEvent("error", "Failed to create file index for cloud.");
      } else {
        newToastEvent(
          "success",
          "Successfully created file index for cloud! You can now chat with files.",
          "temp",
          {
            autoClose: false,
          }
        );
      }

      client.invalidateQueries({ queryKey: ["cloud", data.tenantID] });
    });

    socket.on("backup-error", () => {
      updateToastEvent(
        "error",
        "An error occurred while importing your file.",
        "backup-success",
        {
          loading: false,
          autoClose: false,
        }
      );
    });

    socket.on("backup-progress", (data) => {
      const { progress } = data;

      updateToastEvent(
        "primary",
        `Running Backup... ${progress}%`,
        "backup-success",
        {
          loading: true,
          autoClose: false,
        }
      );
    });

    socket.on("schema-update", (data) => {
      // TODO: Ignore schema update if user updated it themselves (Per tab basis)
      if (subitemDrawerRef.current?.open === true) {
        return;
      }

      /**
       * Ignore schema update only in the client where the drawer is open. For the rest of the clients
       * the schema update will be updated
       */
      if (queryBuilderDrawerRef.current === true) {
        setQueryBuilderUpdatedSchema(true);
        return;
      }

      if (
        (data?.type === "updated-module-modified-submodule" ||
          data?.type === "updated-module-deleted-submodule" ||
          data?.type === "updated-module-created-submodule" ||
          data?.type === "updated-module-reordered-submodules" ||
          data?.type === "modified-module-field") &&
        drawerCountRef.current > 0
      ) {
        return;
      }

      TenantAPI.getLatestSchema().then((schema) => {
        setSchemaRef.current(schema);
      });
    });

    // When a CSV import succeeds or fails
    socket.on("csv-import", (data) => {
      const { type, message, moduleAPINames } = data;

      updateToastEvent(
        type,
        <>
          {message}{" "}
          {!!data?.recordID && (
            <>
              <br />
              <Link
                className="pl-2"
                to={`/clouds/${tenantIDRef.current}/records/FileImports/details/${data.recordID}/Summary`}
              >
                View Import <RightArrow></RightArrow>
              </Link>
            </>
          )}
        </>,
        "csv-import",
        { loading: false, autoClose: false }
      );

      if (moduleAPINames?.length) {
        for (let moduleAPIName of moduleAPINames) {
          client.invalidateQueries({
            queryKey: [tenantIDRef.current, "records", moduleAPIName],
          });
        }
      }
    });

    // When a bulk record deletion succeeds or fails
    socket.on("bulk-deletion", (data) => {
      const { type, message, moduleAPIName, recordIds, tenantID } = data;

      updateToastEvent(type, message, "bulk-deletion", {
        loading: false,
        autoClose: true,
      });

      updateToastEvent(
        type,
        <div className="d-flex justify-content-between align-items-center">
          <div>
            {message}
            <br />
            Refreshing Table...
          </div>
          {type === "success" && (
            <Button
              size="sm"
              color="primary"
              onClick={() =>
                performOperation({
                  Operation: "Restore",
                  RecordIDs: recordIds,
                  onSuccess: () =>
                    client.invalidateQueries({
                      queryKey: [tenantIDRef.current, "records", moduleAPIName],
                    }),
                })
              }
            >
              Undo
            </Button>
          )}
        </div>,
        "bulk-deletion",
        {
          loading: false,
          autoClose: true,
        }
      );

      // Fixes pagination issues
      client.invalidateQueries({
        queryKey: [tenantID, "records", moduleAPIName],
      });
    });

    // When a bulk record update succeeds or fails
    socket.on("bulk-update", (data) => {
      const { type, moduleAPIName, message, recordID } = data;

      updateToastEvent(type, message, "bulk-update", {
        loading: false,
        autoClose: true,
      });

      // A recordID means that a MultiSubitem bulk update occurred.
      // So we need to invalidate the record that stores the subitems.
      if (type === "success" && recordID) {
        client.invalidateQueries({
          queryKey: [tenantIDRef.current, "record", moduleAPIName, recordID],
        });
        client.invalidateQueries({
          queryKey: [tenantIDRef.current, "records", moduleAPIName],
        });
        return;
      }

      client.invalidateQueries({
        queryKey: [tenantIDRef.current, "records", moduleAPIName],
      });
    });

    // When a record is updated
    socket.on("record-update", (data) => {
      const { recordID } = data;

      const queryCache = client.getQueryCache();
      const liveRecordQueries = queryCache.findAll({ type: "active" });

      // Check all active queries for the record
      liveRecordQueries.forEach((query) => {
        // Check for individual record query
        if (query.queryKey.includes(recordID)) {
          // If the record query exists, invalidate it
          client.invalidateQueries({ queryKey: query.queryKey });
        }

        const records: any[] = (query?.state?.data as any)?.records ?? [];

        // Checking search queries that could contain the record
        if (records?.length > 0) {
          records.forEach((record) => {
            // If the record is in the search query, invalidate the search query
            if (record.id === recordID) {
              client.invalidateQueries({ queryKey: query.queryKey });
            }
          });
        }
      });
    });

    // For when the workspace sidebar is updated.
    socket.on("sidebar-updated", (data) => {
      const { workspaceID, sidebar } = data;

      const queryCache = client.getQueryCache();
      const liveRecordQueries = queryCache.findAll({ type: "active" });

      liveRecordQueries.forEach((query) => {
        // Handle individual workspace cache - Key: ["workspace", workspaceID] - Hook: useWorkspace
        if (query.queryKey.includes(workspaceID)) {
          client.setQueryData(query.queryKey, (prev: any) => {
            if (!prev || !prev.workspace) return;

            return {
              ...prev,
              workspace: { ...prev.workspace, Sidebar: sidebar },
            };
          });
        }

        const workspaces: any[] = (query?.state?.data as any)?.workspaces ?? [];

        // Handle workspaces cache - Key: ["workspaces"] - Hook: useWorkspaces
        if (workspaces?.length > 0) {
          workspaces.forEach((workspace) => {
            if (workspace.id === workspaceID) {
              client.setQueryData(query.queryKey, (prev: any) => {
                if (!prev || !prev.workspaces) return;

                return {
                  ...prev,
                  workspaces: prev.workspaces.map((w: any) => {
                    if (w.id === workspaceID) {
                      return { ...w, Sidebar: sidebar };
                    }
                    return w;
                  }),
                };
              });
            }
          });
        }
      });
    });

    // When exporting data
    socket.on("csv-export", (data) => {
      const { Url, message, type } = data;
      window.open(Url, "_self");
      updateToastEvent(type, message, "csv-export", {
        loading: false,
        autoClose: true,
      });
    });

    //---------------------- Supportal Events -------------------------
    // When updating a Module's API name in the supportal
    socket.on("change-module-api-name", (data) => {
      const { message, type, tenantID, oldAPIName, newAPIName } = data;

      updateToastEvent(
        type,
        message,
        `ChangeAPINameProcess-${tenantID}-${oldAPIName}-${newAPIName}`,
        {
          loading: false,
          autoClose: true,
        }
      );
    });

    // When updating a Field's API name in the supportal
    socket.on("rename-field-api-name", (data) => {
      const { message, type, tenantID, oldAPIName, newAPIName } = data;

      updateToastEvent(
        type,
        message,
        `RenameAPINameProcess-${tenantID}-${oldAPIName}-${newAPIName}`,
        {
          loading: false,
          autoClose: true,
        }
      );
    });
  }, []);

  return { startSocketConnection, endSocketConnection, socket };
};

export default useSocketEvents;
